import { useState, useEffect } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { Mail, Send, Loader2, CheckCircle2, XCircle, RefreshCw, AlertTriangle } from 'lucide-react';

function StatusPill({ ok, label }) {
  const color = ok === null ? '#9BAFC0' : ok ? '#22A845' : '#EF4444';
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold font-['JetBrains_Mono'] uppercase tracking-wide"
      style={{ color, border: `1px solid ${color}55`, background: `${color}14` }}>
      {ok === null ? null : ok ? <CheckCircle2 size={10} /> : <XCircle size={10} />}{label}
    </span>
  );
}

export const EmailDeliveryPanel = ({ userEmail }) => {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [to, setTo] = useState(userEmail || '');
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([api.get('/system/email/status'), api.get('/system/email/logs?limit=8')]);
      setStatus(s.data);
      setLogs(l.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load email status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (userEmail && !to) setTo(userEmail); }, [userEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendTest = async () => {
    setSending(true);
    setLastResult(null);
    try {
      const { data } = await api.post('/system/email/test', { to });
      setLastResult(data);
      if (data.sent) toast.success(`Test email sent to ${data.to}`);
      else toast.error(`Email failed: ${data.error}`);
      api.get('/system/email/logs?limit=8').then(r => setLogs(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSending(false);
    }
  };

  const healthy = status?.configured && status?.domain_verified;

  return (
    <div className="rounded-lg p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="email-delivery-panel">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Mail size={18} style={{ color: 'var(--primary)' }} />
          <div>
            <p className="font-bold text-sm font-['Manrope']" style={{ color: 'var(--text-primary)' }}>Email Delivery</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Outbound email via Resend — invites, billing links, repair updates, invoices, password emails</p>
          </div>
        </div>
        <button onClick={load} disabled={loading} className="p-2 rounded-md transition-colors" title="Refresh" style={{ color: 'var(--text-muted)' }} data-testid="email-status-refresh">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {loading && !status ? (
        <div className="flex items-center gap-2 text-xs py-4" style={{ color: 'var(--text-muted)' }}><Loader2 size={14} className="animate-spin" /> Checking provider…</div>
      ) : status && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="rounded-md p-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
              <p className="text-[10px] uppercase tracking-wider font-['JetBrains_Mono'] mb-1.5" style={{ color: 'var(--text-muted)' }}>Provider</p>
              <StatusPill ok={status.configured} label={status.configured ? 'API key set' : 'Not configured'} />
              {status.api_key_hint && <p className="text-[11px] mt-1.5 font-['JetBrains_Mono']" style={{ color: 'var(--text-muted)' }}>Resend · {status.api_key_hint}</p>}
            </div>
            <div className="rounded-md p-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
              <p className="text-[10px] uppercase tracking-wider font-['JetBrains_Mono'] mb-1.5" style={{ color: 'var(--text-muted)' }}>Sending domain</p>
              <StatusPill ok={status.domain_verified} label={status.domain_status || 'unknown'} />
              <p className="text-[11px] mt-1.5 truncate" style={{ color: 'var(--text-muted)' }} title={status.sender} data-testid="email-sender">{status.sender}</p>
            </div>
            <div className="rounded-md p-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
              <p className="text-[10px] uppercase tracking-wider font-['JetBrains_Mono'] mb-1.5" style={{ color: 'var(--text-muted)' }}>Overall</p>
              <StatusPill ok={!!healthy} label={healthy ? 'Ready to send' : 'Needs attention'} />
              {logs && <p className="text-[11px] mt-1.5" style={{ color: 'var(--text-muted)' }}>{logs.total} attempts · {logs.failed} failed</p>}
            </div>
          </div>

          {status.error && (
            <div className="flex items-start gap-2 rounded-md p-3 mb-4 text-xs" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: '#FCA5A5' }} data-testid="email-status-error">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <div className="space-y-1.5">
                <p>{status.error}</p>
                {!status.configured && (
                  <div className="text-[11px]" style={{ color: '#FCD5D5' }} data-testid="email-fix-hint">
                    <p className="font-bold">Nothing can be emailed until this is fixed — invites, billing links, passwords and invoices are all silently skipped.</p>
                    <p className="mt-1">Fix: add these variables to the backend service environment, then redeploy:</p>
                    <ul className="mt-1 font-['JetBrains_Mono'] space-y-0.5">
                      <li>RESEND_API_KEY</li>
                      <li>SENDER_EMAIL</li>
                      <li>PORTAL_URL</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2 mb-2">
            <input
              type="email" value={to} onChange={e => setTo(e.target.value)} placeholder="recipient@example.com"
              className="flex-1 px-3 py-2 rounded-md text-sm outline-none"
              style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
              data-testid="email-test-to"
            />
            <button onClick={sendTest} disabled={sending || !to}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-bold font-['Manrope'] transition-opacity disabled:opacity-50"
              style={{ background: 'var(--primary)', color: '#fff' }} data-testid="email-test-send">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send test email
            </button>
          </div>
          {lastResult && (
            <div className="text-xs rounded-md p-3 mb-3" data-testid="email-test-result"
              style={lastResult.sent
                ? { background: 'rgba(34,168,69,0.08)', border: '1px solid rgba(34,168,69,0.3)', color: '#86EFAC' }
                : { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: '#FCA5A5' }}>
              {lastResult.sent
                ? <>Accepted by Resend for <b>{lastResult.to}</b>{lastResult.resend_id && <> · id <span className="font-['JetBrains_Mono']">{lastResult.resend_id}</span></>}. Check the inbox (and spam) in a minute.</>
                : <>Failed for <b>{lastResult.to}</b>: {lastResult.error}</>}
            </div>
          )}

          {logs && logs.items.length > 0 && (
            <div data-testid="email-log-list">
              <p className="text-[10px] uppercase tracking-wider font-['JetBrains_Mono'] mb-2" style={{ color: 'var(--text-muted)' }}>Recent attempts</p>
              <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
                {logs.items.map((l, i) => (
                  <div key={l.resend_id || `${l.created_at}-${i}`} className="flex items-center gap-3 py-1.5 text-xs">
                    {l.ok ? <CheckCircle2 size={12} className="text-green-500 shrink-0" /> : <XCircle size={12} className="text-red-500 shrink-0" />}
                    <span className="font-['JetBrains_Mono'] shrink-0" style={{ color: 'var(--text-muted)' }}>{new Date(l.created_at).toLocaleString()}</span>
                    <span className="truncate" style={{ color: 'var(--text-primary)' }} title={l.subject}>{l.to} — {l.subject}</span>
                    {!l.ok && l.error && <span className="truncate ml-auto text-red-400" title={l.error}>{l.error}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
